// -- IMPORTS -- //
import {
  ref,
  useRouter,
  watch,
  computed,
  reactive,
  useContext,
} from '@nuxtjs/composition-api';
import shortId from 'shortid';
import bam from '@/lib/bam';
import { AIUpsellEvents } from '@/modules/AI/constants/events';
import {
  getChat,
  getChatStatus,
  sendMessage,
} from '@/modules/AI/services/hriService';
import { useGetArtifactsByThreadQuery } from '@/gql/generated';
import { useApolloQuery } from '@/gql/apolloWrapper';
import { convertThreadToMessages } from './convertThreadToMessages';
import { mapOverviewArtifacts } from './mapOverviewArtifacts';

// -- TYPES & ENUMS -- //
export interface FileUpload {
  name: string;
  fileType: string;
  base64: string;
}

export enum Assistants {
  POLICY = 'policy',
  JOB_DESCRIPTION = 'job-description',
  INCIDENT = 'incident',
  CORRECTIVE_ACTION = 'corrective-action',
  GUIDANCE = 'guidance',
  EDITOR = 'editor',
}

export enum ArtifactTypes {
  POLICY = 'policy',
  JOB_DESCRIPTION = 'jobDescription',
  INCIDENT = 'incident',
  CORRECTIVE_ACTION = 'correctiveAction',
  GUIDANCE = 'guidance',
  BEST_PRACTICE_SUMMARY = 'bestPracticeSummary',
  COMPREHENSIVE_GUIDANCE = 'comprehensiveGuidance',
  ACTION_PLAN = 'actionPlan',
  HR_PRACTICES = 'hrPractices',
  DOCUMENT = 'document',
}

// -- CONSTANTS -- //
export const LOCAL_STORAGE_USER_MESSAGE = 'userMessage';
const DEFAULT_MESSAGE = 'Thinking...';

// -- STATE INITIALIZATION -- //
let initialized = false;
let router;
let context;

const getDefaultChatStatus = () => ({
  loadingMessages: [DEFAULT_MESSAGE],
  updatedAt: new Date(),
});

// -- REACTIVE STATE -- //
const userMessageState = reactive({
  inputText: '',
  artifactId: '',
  artifactNote: '',
  threadId: '',
  thread: [{}],
});

const sessionID = ref();
const isLoading = ref(false);
const hasSendError = ref(false);
const hiddenUserMessage = ref();
const response = ref();
const showRequestChanges = ref(false);
const shouldShowTile = ref(false);
const shouldShowVerified = ref(false);
const solidifyText = ref(false);
const shouldFadeIn = ref(false);
const animateArtifact = ref(false);
const currentThread = ref();
const chatStatus = ref();
const draftingPhrases = ref<string[]>([]);
const hasEnteredDraftState = ref(false);

const lastMessage = reactive({
  content: '',
  isHidden: false,
});

// -- HELPER FUNCTIONS -- //
const filterMessages = (messages: any[]) => {
  return messages.filter((message) => {
    return (
      (message.role === 'assistant' || message.role === 'user') &&
      message.metadata.hidden !== 'true'
    );
  });
};

// -- COMPUTED PROPERTIES -- //
// Helper function to infer assistant type from artifact structure
const inferAssistantFromArtifact = (artifact: any): string => {
  if (!artifact) {
    return '';
  }

  // Check which artifact type exists in the response
  if (artifact.policy) {
    return Assistants.POLICY;
  }
  if (artifact.jobDescription) {
    return Assistants.JOB_DESCRIPTION;
  }
  if (artifact.correctiveAction) {
    return Assistants.CORRECTIVE_ACTION;
  }
  if (artifact.incidents) {
    return Assistants.INCIDENT;
  }
  if (artifact.actionPlan) {
    return Assistants.GUIDANCE;
  }
  if (artifact.document) {
    return Assistants.EDITOR;
  }

  return '';
};

const currentAssistant = computed(() => {
  // First try to use the assistant field from the response
  if (response.value?.assistant) {
    return response.value.assistant;
  }

  // Fallback: infer from artifact structure
  const inferred = inferAssistantFromArtifact(response.value?.artifact);

  return inferred || '';
});

const artifactTypeName = computed(() => {
  switch (currentAssistant.value) {
    case Assistants.POLICY:
      return 'Policy';
    case Assistants.JOB_DESCRIPTION:
      return 'Job Description';
    case Assistants.CORRECTIVE_ACTION:
      return 'Corrective Action';
    case Assistants.INCIDENT:
      return 'Report';
    case Assistants.GUIDANCE:
      return 'Guidance';
    case Assistants.EDITOR:
      return 'Document';
    default:
      return '';
  }
});

const artifactTitle = computed(() => {
  switch (currentAssistant.value) {
    case Assistants.POLICY:
      return response.value?.artifact?.policy?.title || 'your New Policy';
    case Assistants.JOB_DESCRIPTION:
      return (
        response.value?.artifact?.jobDescription?.title ||
        'your Job Description'
      );
    case Assistants.CORRECTIVE_ACTION:
      return response.value?.artifact?.cap?.title || 'your Corrective Action';
    case Assistants.INCIDENT:
      return (
        response.value?.artifact?.incidents?.title || 'your Incident Report'
      );
    case Assistants.GUIDANCE:
      return response.value?.artifact?.actionPlan?.title || 'your HR Guidance';
    default:
      return '';
  }
});

const isCertified = computed(
  () => response.value?.artifact?.policy?.confidence >= 80
);
const employeeId = computed(
  () =>
    response.value?.artifact?.correctiveAction?.employeeId ||
    response.value?.artifact?.jobDescription?.employeeId
);
const employeeList = computed(
  () => response.value?.artifact?.jobDescription?.assignments || []
);

const currentArtifact = computed(() => {
  switch (currentAssistant.value) {
    case Assistants.POLICY:
      return response.value?.artifact?.policy;
    case Assistants.JOB_DESCRIPTION:
      return response.value?.artifact?.jobDescription;
    case Assistants.CORRECTIVE_ACTION:
      return response.value?.artifact?.correctiveAction;
    case Assistants.INCIDENT:
      return response.value?.artifact?.incidents;
    case Assistants.GUIDANCE:
      return response.value?.artifact?.actionPlan;
    case Assistants.EDITOR:
      return response.value?.artifact?.document;
    default:
      return null;
  }
});

const currentArtifactList = computed(() => {
  return response.value?.artifact;
});

const bestPracticeSummaryArtifact = computed(() => {
  return response.value?.artifact?.bestPracticeSummary;
});

const comprehensiveGuidanceArtifact = computed(() => {
  return response.value?.artifact?.comprehensiveGuidance;
});

const actionPlanArtifact = computed(() => {
  return response.value?.artifact?.actionPlan;
});

const hrPracticesArtifact = computed(() => {
  return response.value?.artifact?.hrPractices;
});

const implementationPlanArtifact = computed(() => {
  return response.value?.artifact?.implementationPlan;
});

const correctiveActionArtifact = computed(() => {
  return response.value?.artifact?.correctiveAction;
});

const caseId = computed(() => currentArtifact.value?.caseId || '');
const hrPracticesCaseId = computed(
  () => response.value?.artifact?.hrPractices?.caseId || ''
);
const currentArtifactMarkup = computed(() => currentArtifact.value?.html || '');
const currentGuidanceDto = computed(() => {
  try {
    const dto = JSON.parse(currentArtifact.value?.dto || '{}');

    return dto.guidance || '';
  } catch (e) {
    return '';
  }
});
const currentArtifactId = computed(() => currentArtifact.value?.id || '');
const isEditingartifact = computed(
  () => currentArtifactMarkup.value && isLoading.value
);
const showArtifact = computed(() => !!currentArtifactMarkup.value);
const placeholder = computed(() => response.value?.next?.sample || '');
const canUploadFiles = computed(
  () => response.value?.next?.acceptFileUpload ?? false
);
const activeThread = computed(() => {
  const filtered = filterMessages(response.value?.messages || []);

  return convertThreadToMessages(filtered);
});

const allThinkingPhrases = computed(
  () => chatStatus.value?.loadingMessages || []
);
const currentThinkingPhrase = computed(() => {
  return (
    chatStatus.value?.updatedAt &&
    allThinkingPhrases.value[
      Math.floor(Math.random() * chatStatus.value.loadingMessages.length)
    ]
  );
});

const isDraftingArtifact = computed(() =>
  [
    'pre_policy_change_requested',
    'post_policy_change_requested',
    'pre_policy_drafted',
    'post_policy_drafted',
    'pre_job_description_drafted',
    'post_job_description_drafted',
    'pre_job_description_change_requested',
    'post_job_description_change_requested',
    'pre_corrective_action_drafted',
    'post_corrective_action_drafted',
    'pre_corrective_action_change_requested',
    'post_corrective_action_change_requested',
    'pre_incident_drafted',
    'post_incident_drafted',
    'pre_incident_change_requested',
    'post_incident_change_requested',
    'pre_action_plan_drafted',
    'post_action_plan_drafted',
    'pre_action_plan_change_requested',
    'post_action_plan_change_requested',
    'pre_summary_drafted',
    'post_summary_drafted',
    'policy_drafted',
    'summary_drafted',
    'pre_document_edited',
    'post_document_edited',
    'pre_fetch_document_by_id',
    'post_fetch_document_by_id',
  ].includes(chatStatus.value?.status)
);

// -- WATCHERS -- //
watch(
  isDraftingArtifact,
  (draftState) => {
    if (draftState) {
      hasEnteredDraftState.value = true;
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

watch(
  currentThinkingPhrase,
  (newPhrase) => {
    if (
      newPhrase &&
      hasEnteredDraftState.value &&
      newPhrase !== DEFAULT_MESSAGE
    ) {
      draftingPhrases.value.push(newPhrase);
    }
  },
  { immediate: true }
);

// -- CORE FUNCTIONALITY -- //
const getStatus = async (threadId: any, shouldReset: boolean = false) => {
  if (shouldReset) {
    chatStatus.value = getDefaultChatStatus();
  }
  try {
    const res = await getChatStatus(context.$axios, threadId);
    chatStatus.value = {
      ...res,
      updatedAt: new Date(),
    };
  } catch (e) {
    console.log(e);
  }
};

const getChatOverview = async (threadId: any) => {
  isLoading.value = true;
  userMessageState.threadId = threadId;

  let hasResult = false;

  // Call legacy REST endpoint
  getChat(context.$axios, threadId)
    .then((res) => {
      if (!hasResult) {
        response.value = res;
        hasResult = true;
        isLoading.value = false;
      }
    })
    .catch((_e) => {
      // silent: the GraphQL endpoint below may still succeed
    });

  // Call GraphQL endpoint in parallel
  const artifactsData = ref<any>(null);
  const { onResult, onError } = useApolloQuery(
    useGetArtifactsByThreadQuery,
    {
      input: {
        threadId,
        latestOnly: true,
      },
    },
    { data: artifactsData }
  );

  onResult(() => {
    const mappedArtifacts = mapOverviewArtifacts(
      artifactsData.value?.getArtifacts
    );
    if (!hasResult && mappedArtifacts) {
      response.value = {
        threadId,
        artifact: mappedArtifacts,
        messages: [],
        assistantId: 'n/a',
        runId: 'n/a',
        next: {},
      };
      hasResult = true;
      isLoading.value = false;
    }
  });

  onError(() => {
    // silent: the REST endpoint above may still succeed
  });
};

const chatWithAssistant = async (
  threadId?: string,
  assistant?: string,
  solutionId?: string,
  files?: File[]
) => {
  hasSendError.value = false;
  isLoading.value = true;
  const skipRun = !!(
    !(userMessageState.inputText || hiddenUserMessage.value) && threadId
  );
  lastMessage.content = userMessageState.inputText;
  lastMessage.isHidden = !!hiddenUserMessage.value;

  let statusInterval;

  const runChecks = (shouldReset?) => {
    getStatus(threadId, shouldReset);
  };

  if (threadId) {
    runChecks(true);
    statusInterval = setInterval(
      runChecks,
      hasEnteredDraftState.value ? 6000 : 4000
    );
  } else {
    sessionID.value = shortId.generate();
    bam.trackEvent(AIUpsellEvents.AI_REQUEST_START, {
      type: `${assistant || currentAssistant.value}`,
      sessionID: sessionID.value,
      thread: activeThread.value.length
        ? activeThread.value
        : [userMessageState.inputText || hiddenUserMessage.value],
    });
  }

  const { $axios } = context;

  const content =
    userMessageState.inputText || hiddenUserMessage.value
      ? JSON.stringify({
          content: hiddenUserMessage.value || userMessageState.inputText,
        })
      : undefined;

  try {
    const res = await sendMessage($axios, {
      content,
      threadId,
      assistant,
      files,
      skipRun,
      hidden: !!hiddenUserMessage.value,
    });

    hasSendError.value = false;
    response.value = res;
    userMessageState.threadId = response.value?.threadId;
    currentThread.value = response.value?.threadId || '';
    userMessageState.inputText = '';

    if (!userMessageState.inputText && !threadId) {
      const query = solutionId ? { solutionId } : {};
      router.push({
        path: `/create-request/${res?.threadId}`,
        query,
      });
    } else {
      isLoading.value = false;
    }

    userMessageState.thread = convertThreadToMessages(res?.messages) || [];
    clearInterval(statusInterval);
  } catch (err: any) {
    isLoading.value = false;
    userMessageState.inputText = '';
    hasSendError.value = true;
    console.log(err);
    clearInterval(statusInterval);
    const rawMessage = err?.response?.data?.message;
    const normalizedMessage =
      rawMessage && typeof rawMessage === 'object' && !Array.isArray(rawMessage)
        ? rawMessage.message
        : rawMessage;
    bam.trackEvent(AIUpsellEvents.AI_REQUEST_ERROR, {
      type: assistant || currentAssistant.value,
      sessionID: sessionID.value,
      thread: activeThread.value.length ? activeThread.value : [content],
      errorMessage:
        (Array.isArray(normalizedMessage)
          ? normalizedMessage.join('; ')
          : normalizedMessage) || err?.message,
      errorStatus: err?.response?.status,
    });
  }
};

const handleSendMessage = (
  message?: string | undefined,
  asstId?: string | undefined,
  solutionId?: string | undefined,
  files?: File[] | undefined
) => {
  if (!userMessageState.inputText && !message) {
    return;
  }
  hiddenUserMessage.value = message;
  isLoading.value = true;

  chatWithAssistant(
    currentThread.value,
    asstId || currentAssistant.value,
    solutionId,
    files
  );
  userMessageState.inputText = '';
  hiddenUserMessage.value = '';
};

const resetHook = () => {
  response.value = null;
  currentThread.value = '';
  isLoading.value = false;
  userMessageState.inputText = '';
  userMessageState.artifactId = '';
  userMessageState.threadId = '';
  hasEnteredDraftState.value = false;
  draftingPhrases.value = [];
  animateArtifact.value = false;
  shouldShowTile.value = false;
  shouldFadeIn.value = false;
  hiddenUserMessage.value = '';
  chatStatus.value = null;
  lastMessage.content = '';
  lastMessage.isHidden = false;
  shouldShowVerified.value = false;
  sessionID.value = '';
};

const startNewConversation = (
  message: string | undefined,
  asstId?: string | undefined,
  solutionId?: string | undefined
) => {
  resetHook();
  handleSendMessage(message, asstId, solutionId);
};

const setupHook = () => {
  router = useRouter();
};

// -- HOOK EXPORT -- //
const useAIConversation = () => {
  if (!initialized) {
    setupHook();
    initialized = true;
    context = useContext();
  }

  return {
    // State
    isLoading,
    hasSendError,
    userMessageState,
    showRequestChanges,
    currentThread,
    lastMessage,
    shouldShowTile,
    shouldShowVerified,
    shouldFadeIn,
    animateArtifact,
    solidifyText,
    sessionID,

    // Computed
    currentAssistant,
    showArtifact,
    currentArtifact,
    currentArtifactList,
    bestPracticeSummaryArtifact,
    comprehensiveGuidanceArtifact,
    actionPlanArtifact,
    hrPracticesArtifact,
    implementationPlanArtifact,
    correctiveActionArtifact,
    currentArtifactMarkup,
    currentGuidanceDto,
    currentArtifactId,
    caseId,
    hrPracticesCaseId,
    chatStatus,
    isDraftingArtifact,
    hasEnteredDraftState,
    currentThinkingPhrase,
    allThinkingPhrases,
    draftingPhrases,
    isEditingartifact,
    artifactTitle,
    artifactTypeName,
    employeeId,
    isCertified,
    employeeList,
    canUploadFiles,
    activeThread,
    placeholder,

    // Methods
    handleSendMessage,
    resetHook,
    chatWithAssistant,
    getChatOverview,
    startNewConversation,
  };
};

export default useAIConversation;
